"use client";

import {
  phClearQueuedAnalytics,
  phQueueCapture,
  phQueueIdentity,
  phQueueRegister,
  phQueueRegisterOnce,
} from "./queue";

type Props = Record<string, any> | undefined;
type CaptureOptions = Record<string, unknown> | undefined;
type FeatureFlagOptions = { fresh?: boolean; send_event?: boolean } | undefined;
type FeatureFlagResult = {
  key?: string;
  variant?: string | boolean | null;
  enabled?: boolean;
  payload?: unknown;
} | null;

const NAVIGATION_CAPTURE_DELAY_MS = 150;
const FEATURE_FLAGS_POLL_INTERVAL_MS = 50;
const FEATURE_FLAGS_MAX_WAIT_MS = 3_000;

type FeatureFlagSubscription = { readonly callback: () => void; unsubscribe?: () => void; closed?: true };

let pendingFeatureFlagSubscriptions: FeatureFlagSubscription[] = [];
let featureFlagPollTimer: ReturnType<typeof setTimeout> | null = null;
let featureFlagPollStartTs = 0;

function getPosthog() {
  if (typeof window === "undefined") return null;
  return window.posthog ?? null;
}

export function phCapture(
  event: string,
  properties?: Props,
  options?: CaptureOptions,
): void {
  const ph = getPosthog();
  if (ph?.capture) {
    try {
      ph.capture(event, properties, options);
    } catch {
    }
    return;
  }

  phQueueCapture(event, properties, options);
}

export async function phCaptureForNavigation(
  event: string,
  properties?: Props,
  options?: CaptureOptions,
): Promise<void> {
  const captureOptions = {
    ...(options ?? {}),
    send_instantly: true,
  };
  phCapture(event, properties, captureOptions);

  if (typeof window === "undefined") return;

  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, NAVIGATION_CAPTURE_DELAY_MS);
  });
}

export function trackMarketingCtaClick(props: {
  surface: string;
  cta: string;
  destination: string;
  [key: string]: unknown;
}): void {
  phCapture("marketing_cta_clicked", props);
}

export function phCaptureException(error: Error, properties?: Props): void {
  const ph = getPosthog();
  if (ph?.captureException) {
    try {
      ph.captureException(error, properties);
    } catch {
    }
  }
}

export function phRegister(props: Record<string, any>): void {
  const ph = getPosthog();
  if (ph?.register) {
    try {
      ph.register(props);
    } catch {
    }
    return;
  }
  phQueueRegister(props);
}

export function phRegisterOnce(props: Record<string, any>): void {
  const ph = getPosthog();
  if (ph?.register_once) {
    try {
      ph.register_once(props);
    } catch {
    }
    return;
  }
  phQueueRegisterOnce(props);
}

export function phGetDistinctId(): string | null {
  const ph = getPosthog();
  if (ph?.get_distinct_id) {
    try {
      return ph.get_distinct_id();
    } catch {
      return null;
    }
  }
  return null;
}

export function phGetFeatureFlag(
  flagKey: string,
  options?: FeatureFlagOptions,
): string | boolean | null {
  if (!flagKey) return null;
  const ph = getPosthog();
  if (!ph?.getFeatureFlag) return null;
  try {
    const value = ph.getFeatureFlag(flagKey, options);
    if (value === undefined || value === null) return null;
    if (typeof value === "string" || typeof value === "boolean") return value;
    return null;
  } catch {
    return null;
  }
}

function normalizeFeatureFlagPayload(
  payload: unknown,
): Record<string, unknown> | null {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    return payload as Record<string, unknown>;
  }

  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }

  return null;
}

export function phGetFeatureFlagPayload(
  flagKey: string,
): Record<string, unknown> | null {
  if (!flagKey) return null;
  const ph = getPosthog();
  if (!ph?.getFeatureFlagPayload) return null;
  try {
    return normalizeFeatureFlagPayload(ph.getFeatureFlagPayload(flagKey));
  } catch {
    return null;
  }
}

export function phGetFeatureFlagResult(
  flagKey: string,
  options?: FeatureFlagOptions,
): FeatureFlagResult {
  if (!flagKey) return null;
  const ph = getPosthog();
  if (!ph?.getFeatureFlagResult) return null;
  try {
    const result = ph.getFeatureFlagResult(flagKey, options);
    if (!result || typeof result !== "object" || Array.isArray(result)) {
      return null;
    }
    return {
      ...(result as Exclude<FeatureFlagResult, null>),
      payload: normalizeFeatureFlagPayload(
        (result as { payload?: unknown }).payload,
      ),
    };
  } catch {
    return null;
  }
}

function clearFeatureFlagPolling(): void {
  if (featureFlagPollTimer !== null) {
    clearTimeout(featureFlagPollTimer);
    featureFlagPollTimer = null;
  }
  featureFlagPollStartTs = 0;
}

function registerPendingFeatureFlagCallbacks(
  ph: NonNullable<ReturnType<typeof getPosthog>>,
): void {
  const subscriptions = pendingFeatureFlagSubscriptions;
  pendingFeatureFlagSubscriptions = [];
  clearFeatureFlagPolling();

  for (const subscription of subscriptions) subscribeFeatureFlags(ph, subscription);
}

function subscribeFeatureFlags(
  ph: NonNullable<ReturnType<typeof getPosthog>>,
  subscription: FeatureFlagSubscription,
): void {
  try {
    subscription.unsubscribe = ph.onFeatureFlags?.(subscription.callback);
  } catch {
  }
  if (subscription.closed) subscription.unsubscribe?.();
}

function scheduleFeatureFlagPolling(): void {
  if (typeof window === "undefined") return;
  if (featureFlagPollTimer !== null) return;
  if (featureFlagPollStartTs === 0) {
    featureFlagPollStartTs = Date.now();
  }

  const pollForPosthog = () => {
    featureFlagPollTimer = null;

    const ph = getPosthog();
    if (ph?.onFeatureFlags) {
      registerPendingFeatureFlagCallbacks(ph);
      return;
    }

    if (pendingFeatureFlagSubscriptions.length === 0) {
      clearFeatureFlagPolling();
      return;
    }

    if (Date.now() - featureFlagPollStartTs >= FEATURE_FLAGS_MAX_WAIT_MS) {
      pendingFeatureFlagSubscriptions = [];
      clearFeatureFlagPolling();
      return;
    }

    featureFlagPollTimer = setTimeout(
      pollForPosthog,
      FEATURE_FLAGS_POLL_INTERVAL_MS,
    );
  };

  featureFlagPollTimer = setTimeout(
    pollForPosthog,
    FEATURE_FLAGS_POLL_INTERVAL_MS,
  );
}

export function phOnFeatureFlags(callback: () => void): () => void {
  const subscription: FeatureFlagSubscription = { callback };
  const unsubscribe = () => {
    subscription.closed = true;
    pendingFeatureFlagSubscriptions = pendingFeatureFlagSubscriptions.filter((pending) => pending !== subscription);
    subscription.unsubscribe?.();
    subscription.unsubscribe = undefined;
  };
  if (typeof callback !== "function") return unsubscribe;
  if (typeof window === "undefined") return unsubscribe;

  const ph = getPosthog();
  if (ph?.onFeatureFlags) {
    subscribeFeatureFlags(ph, subscription);
    return unsubscribe;
  }

  pendingFeatureFlagSubscriptions.push(subscription);
  scheduleFeatureFlagPolling();
  return unsubscribe;
}

export function phAlias(newDistinctId: string): void {
  const ph = getPosthog();
  if (ph?.alias) {
    try {
      ph.alias(newDistinctId);
    } catch {
    }
  }
}

const sentIdentityPayloads = new Map<string, string>();

function identityShapeAndPayload(distinctId: string, properties?: Props): [string, string] {
  const keys = Object.keys(properties ?? {}).sort();
  return [
    `${distinctId}\n${keys.join(",")}`,
    JSON.stringify(keys.map((key) => properties?.[key] ?? null)),
  ];
}

export function phIdentify(distinctId: string, properties?: Props): void {
  const ph = getPosthog();
  if (ph?.identify) {
    const [shape, payload] = identityShapeAndPayload(distinctId, properties);
    if (sentIdentityPayloads.get(shape) === payload) return;
    try {
      ph.identify(distinctId, properties);
      sentIdentityPayloads.set(shape, payload);
    } catch {
    }
    return;
  }
  phQueueIdentity(distinctId, properties);
}

export function phReset(): void {
  sentIdentityPayloads.clear();
  clearPendingFilterCaptures();
  phClearQueuedAnalytics();
  const ph = getPosthog();
  if (ph?.reset) {
    try {
      ph.reset();
    } catch {
    }
  }
}

export function phStartSessionRecording(): void {
  const ph = getPosthog();
  if (ph?.startSessionRecording) {
    try {
      ph.startSessionRecording(true);
    } catch {
    }
  }
}

export function phStopSessionRecording(): void {
  const ph = getPosthog();
  if (ph?.stopSessionRecording) {
    try {
      ph.stopSessionRecording();
    } catch {
    }
  }
}

export function phIsInitialized(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(window.__posthogInitialized);
}

export function trackBetLinkClick(props: {
  sportsbook: string;
  page: "optimizer" | "whale-watch" | "arbitrage" | "low-hold" | "odds-screen" | "esports-discrepancies" | "ev-sniper";
  bet_type: "gameline" | "prop" | "arb" | "low-hold";
  odds_american?: number;
  ev_percentage?: number;
  league?: string;
  click_source?: "cta" | "odds_badge" | "peer_grid" | "peer_grid_muted" | "empty_state" | "board" | "slip";
}) {
  phCapture("bet_link_clicked", props);
  phRegisterOnce({ first_bet_link_click_page: props.page });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("upsider:bet_link_clicked"));
  }
}

export type ToolAnalyticsName =
  | "optimizer"
  | "props"
  | "ev-sniper"
  | "whale-watch"
  | "arbitrage"
  | "low-hold"
  | "odds-screen"
  | "esports-discrepancies";

export type ToolRowKind =
  | "gameline"
  | "prop"
  | "edge"
  | "opportunity"
  | "arb"
  | "low-hold";

export function trackToolRowExpanded(props: {
  tool: ToolAnalyticsName;
  row_kind: ToolRowKind;
}): void {
  phCapture("tool_row_expanded", props);
}

export function trackToolSortChanged(props: {
  tool: ToolAnalyticsName;
  column: string;
  direction: "asc" | "desc";
}): void {
  phCapture("tool_sort_changed", props);
}

const DEBOUNCED_FILTER_NAMES = new Set(["player_search", "league"]);
const FILTER_CHANGED_DEBOUNCE_MS = 400;
const pendingFilterTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearPendingFilterCaptures(): void {
  for (const timer of pendingFilterTimers.values()) {
    clearTimeout(timer);
  }
  pendingFilterTimers.clear();
}

export function trackFilterChanged(props: {
  filter_name: string;
  filter_value: string;
  page: string;
  source?: string;
  debounce_ms?: number;
}): void {
  const { debounce_ms, ...captureProps } = props;
  const delay = debounce_ms ?? (
    DEBOUNCED_FILTER_NAMES.has(props.filter_name) ? FILTER_CHANGED_DEBOUNCE_MS : 0
  );
  if (delay <= 0) {
    phCapture("filter_changed", captureProps);
    return;
  }
  const key = `${props.page}:${props.filter_name}`;
  const existing = pendingFilterTimers.get(key);
  if (existing) clearTimeout(existing);
  pendingFilterTimers.set(
    key,
    setTimeout(() => {
      pendingFilterTimers.delete(key);
      phCapture("filter_changed", captureProps);
    }, delay),
  );
}

export type BetDismissalAnalyticsProps = {
  fingerprint: string;
  page: "optimizer" | "whale-watch" | "low-hold" | "arbitrage" | "odds-screen" | "esports-discrepancies" | "ev-sniper";
  source?: string;
  sport?: string;
  league?: string;
  event_id?: string | number;
  entity_id?: string | number;
  market?: string;
  line?: string | number | null;
  side?: string;
  [key: string]: unknown;
};

export function trackBetDismissed(props: BetDismissalAnalyticsProps): void {
  phCapture("bet_dismissed", props);
}

export function trackBetRestored(props: BetDismissalAnalyticsProps): void {
  phCapture("bet_restored", props);
}
